import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, ButtonProps, IconButton, Skeleton } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons/linear';

import { ScrollableWrapperProps } from './scrollable-wrapper.props';

import { useThrottledCallback } from '../../../hooks/use-throttled-callback.hook';

export const ScrollableWrapper = ({
    children,
    showArrows,
    scrollableContainerProps,
    loading = false,
    arrowOffset,
    scrollOffset,
}: ScrollableWrapperProps): JSX.Element => {
    const scrollableRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [arrowVisibility, setArrowVisibility] = useState({
        left: false,
        right: false,
    });

    const updateArrowVisibility = useCallback(() => {
        const scrollContainer = scrollableRef.current;
        if (!scrollContainer) return;

        const SCROLL_TOLERANCE = 1;
        const scrollLeftValue = scrollContainer.scrollLeft;
        const remainingScroll =
            scrollContainer.scrollWidth - scrollContainer.clientWidth - scrollLeftValue;

        const left = scrollLeftValue > SCROLL_TOLERANCE;
        const right = remainingScroll > SCROLL_TOLERANCE;

        setArrowVisibility(prev =>
            prev.left === left && prev.right === right ? prev : { left, right },
        );
    }, []);

    const handleArrowScroll = useCallback((direction: 'left' | 'right') => {
        const scrollContainer = scrollableRef.current;
        if (!scrollContainer) return;

        const scrollAmount = scrollOffset || scrollContainer.clientWidth * 0.3;
        const amount = direction === 'left' ? -scrollAmount : scrollAmount;
        scrollContainer.scrollBy({ left: amount, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        if (!showArrows) return;

        const scrollContainer = scrollableRef.current;
        if (!scrollContainer) return;

        // ResizeObserver fires once right after `observe()`, which covers the initial
        // measurement without forcing a synchronous layout during hydration.
        scrollContainer.addEventListener('scroll', updateArrowVisibility, { passive: true });
        const resizeObserver = new ResizeObserver(updateArrowVisibility);
        resizeObserver.observe(scrollContainer);

        return () => {
            scrollContainer.removeEventListener('scroll', updateArrowVisibility);
            resizeObserver.disconnect();
        };
    }, [updateArrowVisibility, showArrows]);

    useEffect(() => {
        // FIX DNI-742: Sometimes initial scroll position was not at the beginning.
        if (scrollableRef.current) {
            scrollableRef.current.scrollLeft = 0;
        }
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollableRef.current) return;

        setIsDragging(true);
        setStartX(e.pageX - scrollableRef.current.offsetLeft);
        setScrollLeft(scrollableRef.current.scrollLeft);
        if (scrollableRef.current) {
            scrollableRef.current.style.cursor = 'grabbing';
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        if (scrollableRef.current) {
            scrollableRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
        if (scrollableRef.current) {
            scrollableRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseMove = useThrottledCallback((e: React.MouseEvent) => {
        if (!isDragging || !scrollableRef.current) return;
        e.preventDefault();

        const x = e.pageX - scrollableRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        scrollableRef.current.scrollLeft = scrollLeft - walk;
    }, 16);

    const arrowButtonStyles: ButtonProps = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        h: 10,
        w: 10,
        boxShadow: '2xl',
        borderRadius: 'full',
        bg: 'white',
        color: 'gray.900',
        border: 'none',
        _hover: {
            bg: 'gray.200',
        },
    };

    return (
        <Box position="relative" overflow={arrowOffset ? 'visible' : 'hidden'}>
            {arrowVisibility.left && (
                <Skeleton asChild loading={loading}>
                    <IconButton
                        onClick={() => handleArrowScroll('left')}
                        left={arrowOffset ?? 3}
                        {...arrowButtonStyles}
                    >
                        <IconsLinear.ArrowLeft3 scale={1.3} mr={'2px'} />
                    </IconButton>
                </Skeleton>
            )}
            <Box
                ref={scrollableRef}
                display={'grid'}
                gap={'1rem'}
                gridTemplateRows={'auto'}
                gridAutoFlow={'column'}
                scrollSnapType={'x mandatory'}
                overflowX={'auto'}
                marginLeft={'-1rem'}
                padding={'1rem 1rem 0'}
                width={'100vw'}
                scrollbar="hidden"
                css={{
                    '> *': {
                        scrollSnapAlign: 'center',
                    },
                    cursor: 'grab',
                    userSelect: 'none', // Prevent text selection during drag
                }}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                {...scrollableContainerProps}
            >
                {children}
            </Box>
            {arrowVisibility.right && (
                <Skeleton asChild loading={loading}>
                    <IconButton
                        onClick={() => handleArrowScroll('right')}
                        right={arrowOffset ?? 3}
                        {...arrowButtonStyles}
                    >
                        <IconsLinear.ArrowRight3 scale={1.3} ml={'2px'} />
                    </IconButton>
                </Skeleton>
            )}
        </Box>
    );
};
